import React, { useState, useEffect } from "react";
import * as i18nUtils from "util/i18n";
import { connect } from "react-redux";
import { selectors as configSelectors } from "reducers/config";
import { selectors as i18nSelectors } from "reducers/i18n";

import FieldLabel from "pages/_components/fields/FieldLabel";
import ReactPhoneInput from "react-phone-input-2";
import classNames from "classnames";
import { shape, string, bool, func } from "prop-types";

import es from "react-phone-input-2/lang/es.json";
import pt from "react-phone-input-2/lang/pt.json";
import { usePhoneAreaCodesArg } from "../../../hooks/usePhoneAreaCodesArg";
import FieldError from "./FieldError";

import "react-phone-input-2/lib/style.css";
import "react-phone-input-2/lib/bootstrap.css";
import FieldHint from "./FieldHint";

const PhoneInput = (props) => {
    const {
        mobilePhone,
        country,
        idForm,
        form,
        field,
        labelText,
        labelTextDefault,
        hintLabel,
        hideLabel,
        activeLanguage,
        configs,
        phoneAlreadyUpdated,
        updatePhone,
    } = props;
    const [isFirstTime, setIsFirstTime] = useState(true);
    const [value, setValue] = useState("1");
    const [valueData, setValueData] = useState({ dialCode: "1" });
    const [alreadyReset, setAlreadyReset] = useState(true);
    const { argAreaCodes } = usePhoneAreaCodesArg();
    const [isFocused, toggleIsFocused] = useState(false);

    let { idField } = props;
    if (!idField) {
        idField = `${idForm}.${field.name}`;
    }

    useEffect(() => {
        if (updatePhone) {
            setValue(field?.value?.countryCode + field.value?.areaCode + field?.value?.value);
            phoneAlreadyUpdated();
        }
    }, [updatePhone]);

    useEffect(() => {
        if (isFirstTime) {
            if (mobilePhone && mobilePhone.length > 0) {
                setValue(mobilePhone);
            }

            if (field && field?.value?.value) {
                setValue(
                    field?.value?.prefix || field?.value?.countryCode + field.value?.areaCode + field?.value?.value,
                );
            }
            setIsFirstTime(false);
        }
    }, [isFirstTime, mobilePhone, field, setIsFirstTime]);

    useEffect(() => {
        if (field.value?.value === "" && field.value?.prefix === "" && !alreadyReset) {
            setValue(mobilePhone || "");
            setAlreadyReset(true);
        }
    }, [alreadyReset, field.value, mobilePhone]);

    useEffect(() => {
        if (field.value?.countryCode === "" && field.value?.areaCode === "" && field.value?.value === "") {
            setValue(mobilePhone || "");
            setValueData({ dialCode: "1" });
        }
    }, [field.value]);

    const masks = () => {
        const dbMasks = Object.keys(configs).filter((config) => config.includes("phone.mask"));
        const customMasks = {};
        dbMasks.map((mask) => {
            const countryMask = mask.split(".")[2].toLowerCase();
            if (countryMask) {
                customMasks[countryMask] = configs[mask];
            }
            return customMasks;
        });
        return customMasks;
    };

    const location = () => {
        if (activeLanguage === "es") {
            return es;
        }
        if (activeLanguage === "pt") {
            return pt;
        }
        return undefined;
    };

    const handleOnChange = (valueChanged, data, e, formattedValue) => {
        const { dialCode: previousDialCode } = valueData;
        const { dialCode } = data;
        // const { format } = data;
        let phoneValue = valueChanged;
        if (previousDialCode !== dialCode) {
            phoneValue = dialCode;
        } else if (valueChanged.length === 0) {
            phoneValue = dialCode;
        }
        setValue(phoneValue);
        setValueData(data);

        let dialCodeValue = dialCode;
        if (form) {
            let areaCode = "";
            if (formattedValue.includes(" ") && data.countryCode === "ar") {
                // eslint-disable-next-line prefer-destructuring
                areaCode = formattedValue.split(" ")[1];
                if (dialCode.includes(areaCode)) {
                    dialCodeValue = dialCode.substring(0, dialCode.length - areaCode.length);
                }
            }
            phoneValue = phoneValue.substring(dialCodeValue.length + areaCode.length);

            if (phoneValue.includes("-")) {
                phoneValue.replace("-", "");
            }

            form.setFieldValue(field.name, { countryCode: dialCodeValue, areaCode, value: phoneValue });
            form.setFieldTouched(field.name);
        }
        setAlreadyReset(false);
    };

    const handleOnKeyDown = (e) => {
        const { dialCode } = valueData;
        const { value: targetValue } = e.currentTarget;
        const extraCharacter = targetValue.includes("(") ? 3 : 1;
        if (
            dialCode.length + extraCharacter > e.currentTarget.selectionStart &&
            targetValue.length === e.currentTarget.selectionEnd
        ) {
            e.currentTarget.selectionStart = dialCode.length + extraCharacter;
        } else if (
            dialCode.length + extraCharacter > e.currentTarget.selectionStart &&
            e.currentTarget.selectionStart === e.currentTarget.selectionEnd
        ) {
            e.currentTarget.selectionStart = targetValue.length;
        }
        if (e.keyCode === 8 && dialCode && dialCode.length > 0 && dialCode.length === value.length) {
            e.preventDefault();
        }
    };

    const handleIsValid = (validValue, validCountry) => {
        const { countryCode, dialCode, format, name } = validCountry;
        const { dialCode: previousDialCode } = valueData;
        if (previousDialCode !== dialCode) {
            setValue(validValue);
            setValueData({
                countryCode,
                dialCode,
                format,
                name,
            });
        }
        return true;
    };

    const handleErrors = (errors) => {
        if (typeof errors === "string") {
            return (
                <div className="form-group has-error">
                    <FieldError error={errors} />
                </div>
            );
        }
        return Object.keys(errors).map((error) => (
            <div className="form-group has-error">
                <FieldError error={errors[error]} />
            </div>
        ));
    };

    const handleFocus = () => {
        toggleIsFocused(true);
    };
    const handleBlur = () => {
        toggleIsFocused(false);
        form.setFieldTouched(field.name);
    };

    const { errors, touched } = form;
    const wasTouched = touched && touched[field.name];
    const hasError = errors && errors[field.name] && wasTouched;
    if (!idField) {
        idField = `${idForm}.${field.name}`;
    }

    const fieldLabel = labelText || `${idForm}.${field.name}.label`;

    return (
        <div
            className={classNames("form-group", {
                "has-error": hasError,
                "has-focus": isFocused,
            })}>
            <FieldLabel
                idField={`${idField}`}
                labelKey={fieldLabel}
                labelText={labelTextDefault}
                hideLabel={hideLabel}
            />
            <ReactPhoneInput
                key={argAreaCodes}
                inputProps={{ id: idField }}
                inputStyle={{ width: "100%" }}
                searchStyle={{ width: "92%" }}
                onChange={handleOnChange}
                onKeyDown={handleOnKeyDown}
                onFocus={handleFocus}
                onBlur={handleBlur}
                isValid={handleIsValid}
                searchPlaceholder={i18nUtils.get("settings.changePhone.mobilePhone.search.placeholder")}
                searchNotFound={i18nUtils.get("settings.changePhone.mobilePhone.search.countryNotFound")}
                country={country}
                localization={location()}
                value={value}
                masks={masks()}
                preferredCountries={["us"]}
                areaCodes={{
                    ar: argAreaCodes,
                }}
                enableAreaCodes
                enableAreaCodeStretch
                enableSearch
                containerClass="input-group-standard  container-phone-input"
                buttonClass="button-phone-input"
                dropdownClass="dropdown-phone-input"
                searchClass="search-phone-input"
                {...props}
            />
            {hasError && handleErrors(errors[field.name])}
            {hintLabel && <FieldHint text={hintLabel} />}
        </div>
    );
};

PhoneInput.propTypes = {
    idForm: string.isRequired,
    form: shape({}).isRequired,
    idField: string.isRequired,
    field: shape({}).isRequired,
    mobilePhone: string.isRequired,
    activeLanguage: string.isRequired,
    configs: shape({}).isRequired,
    country: string,
    hideLabel: bool,
    labelText: string,
    labelTextDefault: string,
    hintLabel: string,
    updatePhone: bool,
    phoneAlreadyUpdated: func,
};

PhoneInput.defaultProps = {
    country: "us",
    hideLabel: false,
    labelText: "",
    labelTextDefault: "",
    hintLabel: "",
    updatePhone: false,
    phoneAlreadyUpdated: () => {},
};

const mapStateToProps = (state) => ({
    activeLanguage: i18nSelectors.getLang(state),
    configs: configSelectors.getConfig(state),
});

export default connect(mapStateToProps)(PhoneInput);
